import {useEffect, useState} from "react";
import {toast} from "react-toastify";
import MainLayout from "../components/MainLayout";
import {InputText} from "primereact/inputtext";
import {Button} from "primereact/button";
import {DataTable} from "primereact/datatable";
import {Column} from "primereact/column";
import {Dialog} from "primereact/dialog";
import {Pencil, Trash2} from "lucide-react";
import {
	createBlockOfStorage,
	deleteBlockOfStorage,
	editBlockOfStorage,
	getBlocksOfStorage,
} from "../services/block.service";

const BlocksPage = () => {
	const [blocks, setBlocks] = useState([]);
	const [loading, setLoading] = useState(false);
	const [showDeleteDialog, setShowDeleteDialog] = useState(false);
	const [selectedBlock, setSelectedBlock] = useState(null);
	const [globalFilter, setGlobalFilter] = useState("");
	const [showEditDialog, setShowEditDialog] = useState(false);
	const [showAddDialog, setShowAddDialog] = useState(false);
	const [editBlock, setEditBlock] = useState({
		id: null,
		name: "",
		is_active: true,
	});
	const [newBlock, setNewBlock] = useState({
		name: "",
		is_active: true,
	});

	useEffect(() => {
		fetchBlocks();
	}, []);

	const fetchBlocks = async () => {
		setLoading(true);
		try {
			const {blocks} = await getBlocksOfStorage();
			setBlocks(blocks);
		} catch (error) {
			toast.error("Блокларни юклашда хатолик юз берди");
		} finally {
			setLoading(false);
		}
	};

	const handleEdit = (block) => {
		setEditBlock({
			id: block.id,
			name: block.name,
			is_active: block.is_active,
		});
		setShowEditDialog(true);
	};

	const handleDeleteSelection = (block) => {
		setSelectedBlock(block);
		setShowDeleteDialog(true);
	};

	const handleDelete = async () => {
		try {
			await deleteBlockOfStorage(selectedBlock.id);
			setBlocks((prev) => prev.filter((b) => b.id !== selectedBlock.id));
			toast.success("Блок муваффақиятли ўчирилди");
		} catch (error) {
			toast.error("Блокни ўчиришда хатолик");
		} finally {
			setShowDeleteDialog(false);
			setSelectedBlock(null);
		}
	};

	const handleSearch = (e) => {
		setGlobalFilter(e.target.value);
	};

	const handleAddNewBlock = () => {
		setNewBlock({name: "", is_active: true});
		setShowAddDialog(true);
	};

	const handleSaveNewBlock = async () => {
		if (!newBlock.name.trim()) {
			toast.error("Блок номини киритиш шарт");
			return;
		}

		try {
			const response = await createBlockOfStorage(newBlock);
			setBlocks((prev) => [...prev, response.block]);
			toast.success("Янги блок муваффақиятли қўшилди");
			setShowAddDialog(false);
		} catch (error) {
			console.log(error);
			toast.error("Янги блок қўшишда хатолик юз берди");
		}
	};

	const handleSaveEditBlock = async () => {
		if (!editBlock.name.trim()) {
			toast.error("Блок номини киритиш шарт");
			return;
		}

		try {
			const response = await editBlockOfStorage(
				editBlock.id,
				editBlock.name,
				editBlock.is_active
			);

			setBlocks((prev) =>
				prev.map((block) =>
					block.id === editBlock.id ? response.block : block
				)
			);

			toast.success("Блок муваффақиятли таҳрирланди");
			setShowEditDialog(false);
		} catch (error) {
			toast.error("Блокни таҳрирлашда хатолик юз берди");
		}
	};

	return (
		<MainLayout header="Блоклар">
			<div>
				<div className="flex justify-between mb-4">
					<div className="relative flex items-center w-full">
						<i className="absolute text-gray-400 -translate-y-1/2 pointer-events-none pi pi-search left-3 top-1/2" />
						<InputText
							className="w-full px-4 py-2 pb-1 border pl-9"
							placeholder="Блок номи ёки ID рақами бўйича қидиринг..."
							value={globalFilter}
							onChange={handleSearch}
						/>
					</div>
					<Button
						icon="pi pi-plus"
						label="Қўшиш"
						className="px-4 text-white min-w-[100px] bg-blue rounded-md hover:bg-opacity-90 w-[200px] ml-4"
						onClick={handleAddNewBlock}
					/>
				</div>

				<DataTable
					className="custom-row-border"
					value={blocks}
					emptyMessage="Ҳеч қандай блок топилмади"
					loading={loading}
					rows={10}
					rowsPerPageOptions={[10, 25, 50]}
					globalFilter={globalFilter}
					dataKey="id"
					style={{
						"--hover-bg-color": "transparent",
					}}>
					<Column field="id" header="ID" className="w-[250px]" />
					<Column field="name" header="Блок номи" className="w-[350px]" />
					<Column
						field="is_active"
						header="Фаоллик"
						className="w-[350px]"
						body={(rowData) => (rowData.is_active ? "Актив" : "Ноактив")}
					/>

					<Column
						header="Амаллар"
						body={(rowData) => (
							<div className="flex items-center gap-8 cursor-pointer">
								<Pencil onClick={() => handleEdit(rowData)} />
								<Trash2 onClick={() => handleDeleteSelection(rowData)} />
							</div>
						)}
						className="text-right w-[100px]"
					/>
				</DataTable>
			</div>

			<Dialog
				visible={showDeleteDialog}
				onHide={() => setShowDeleteDialog(false)}
				header={
					<div className="text-lg font-semibold text-black">Блокни ўчириш</div>
				}
				className="border-t-[6px] border-red-600 rounded-md shadow-lg"
				style={{width: "30rem"}}
				footer={
					<div className="flex justify-between mt-4">
						<Button
							label="Бекор қилиш"
							icon="pi pi-times"
							className="px-4 py-2 text-black bg-gray-200 hover:bg-gray-300"
							onClick={() => setShowDeleteDialog(false)}
						/>
						<Button
							label="Ўчириш"
							icon="pi pi-trash"
							className="px-4 py-2 text-white bg-red-600 hover:bg-red-700"
							onClick={handleDelete}
						/>
					</div>
				}>
				<p className="text-gray-700">
					<strong className="text-black">{selectedBlock?.name}</strong> номли
					блокни ўчирмоқчимисиз?
				</p>
			</Dialog>

			<Dialog
				visible={showEditDialog}
				onHide={() => setShowEditDialog(false)}
				header="Блокни таҳрирлаш"
				footer={
					<div className="flex justify-between mt-2 min-w-[400px]">
						<Button
							label="Бекор қилиш"
							icon="pi pi-times"
							className="p-button-text"
							onClick={() => setShowEditDialog(false)}
						/>
						<Button
							label="Сақлаш"
							icon="pi pi-check"
							className="p-button-success"
							onClick={handleSaveEditBlock}
						/>
					</div>
				}>
				<div className="mt-4 p-field">
					<label htmlFor="blockName" className="block mb-2 font-medium">
						Блок номи
					</label>
					<InputText
						id="blockName"
						value={editBlock.name}
						onChange={(e) => setEditBlock({...editBlock, name: e.target.value})}
						className="w-full p-2 border"
					/>
				</div>
				<div className="mt-4 p-field">
					<div className="flex items-center">
						<input
							type="checkbox"
							id="blockStatus"
							checked={editBlock.is_active}
							onChange={(e) =>
								setEditBlock({...editBlock, is_active: e.target.checked})
							}
							className="mr-2"
						/>
						<label htmlFor="blockStatus">Актив</label>
					</div>
				</div>
			</Dialog>

			<Dialog
				visible={showAddDialog}
				onHide={() => setShowAddDialog(false)}
				header="Янги блок қўшиш"
				footer={
					<div className="flex justify-between mt-2 min-w-[400px]">
						<Button
							label="Бекор қилиш"
							icon="pi pi-times"
							className="p-button-text"
							onClick={() => setShowAddDialog(false)}
						/>
						<Button
							label="Қўшиш"
							icon="pi pi-check"
							className="p-button-success"
							onClick={handleSaveNewBlock}
						/>
					</div>
				}>
				<div className="mt-4 p-field">
					<label htmlFor="newBlockName" className="block mb-2 font-medium">
						Блок номи
					</label>
					<InputText
						id="newBlockName"
						value={newBlock.name}
						onChange={(e) => setNewBlock({...newBlock, name: e.target.value})}
						className="w-full p-2 border"
					/>
				</div>
				<div className="mt-4 p-field">
					<div className="flex items-center">
						<input
							type="checkbox"
							id="newBlockStatus"
							checked={newBlock.is_active}
							onChange={(e) =>
								setNewBlock({...newBlock, is_active: e.target.checked})
							}
							className="mr-2"
						/>
						<label htmlFor="newBlockStatus">Актив</label>
					</div>
				</div>
			</Dialog>
		</MainLayout>
	);
};

export default BlocksPage;
